import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  FileText,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Building2,
  Eye,
  RefreshCw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

import { financeAPI } from '@/shared/services/api';

const statusConfig = {
  pending: { label: 'Pending', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400', icon: Clock },
  filed: { label: 'Filed', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400', icon: FileText },
  paid: { label: 'Paid', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400', icon: CheckCircle2 },
  overdue: { label: 'Overdue', class: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400', icon: AlertTriangle }
};

export const TaxManagement = () => {const { t } = useTranslation("finance");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchObligations = async () => {
    setLoading(true);
    try {
      const data = await financeAPI.getTaxObligations(activeTab === 'all' ? null : activeTab);
      setObligations(data);
    } catch (err) {
      toast.error('Failed to load tax obligations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObligations();
  }, [activeTab]);

  const stats = {
    total: obligations.length,
    pending: obligations.filter((t) => t.status === 'pending').length,
    pendingAmount: obligations.filter((t) => t.status === 'pending').reduce((sum, t) => sum + (t.amount || 0), 0),
    paidAmount: obligations.filter((t) => t.status === 'paid').reduce((sum, t) => sum + (t.amount || 0), 0)
  };

  const filteredObligations = obligations.filter((obligation) => {
    if (!searchTerm) return true;
    return obligation.tax_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obligation.period?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-tax-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("taxManagement", "Tax Management")}</h1>
          <p className="text-muted-foreground mt-1">{t("trackTaxObligations", "Track tax obligations and filings")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchObligations} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />{t("addObligation", "Add Obligation")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <FinanceStatCard
          title="Total Obligations"
          value={stats.total}
          icon={FileText}
          color="blue" />
        
        <FinanceStatCard
          title="Pending"
          value={stats.pending}
          icon={Clock}
          color="gold" />
        
        <FinanceStatCard
          title="Amount Due"
          value={stats.pendingAmount}
          icon={AlertTriangle}
          color="red"
          prefix="$" />
        
        <FinanceStatCard
          title="Paid YTD"
          value={stats.paidAmount}
          icon={CheckCircle2}
          color="emerald"
          prefix="$" />
        
      </div>

      {/* Tax Calendar Alert */}
      <Card className="card-elevated bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-950/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <Calendar className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{t("upcomingDeadlines", "Upcoming Deadlines")}</h3>
              <p className="text-sm text-muted-foreground">{t("youHave", "You have")}
                {stats.pending}{t("taxObligationsDue", "tax obligations due. Next deadline:")}{
                obligations.filter((t) => t.status === 'pending').sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0]?.due_date || 'N/A'
                }
              </p>
            </div>
            <Button variant="outline">{t("viewCalendar", "View Calendar")}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="pending">{t("pending", "Pending")}</TabsTrigger>
                <TabsTrigger value="filed">{t("filed", "Filed")}</TabsTrigger>
                <TabsTrigger value="paid">{t("paid", "Paid")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by tax type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("taxType", "Tax Type")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("period", "Period")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredObligations.map((obligation) => {
                  const StatusIcon = statusConfig[obligation.status]?.icon || Clock;
                  const isOverdue = new Date(obligation.due_date) < new Date() && obligation.status === 'pending';
                  return (
                    <tr key={obligation.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{obligation.tax_type}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm">{obligation.period}</td>
                      <td className="py-4 px-4 text-right font-mono font-medium">
                        ${obligation.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Calendar className={`h-3.5 w-3.5 ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`} />
                          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{obligation.due_date}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Badge className={isOverdue ? statusConfig.overdue.class : statusConfig[obligation.status]?.class || statusConfig.pending.class}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {isOverdue ? 'Overdue' : statusConfig[obligation.status]?.label || obligation.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {obligation.status === 'pending' &&
                          <Button size="sm" variant="outline">{t("file", "File")}</Button>
                          }
                          {obligation.status === 'filed' &&
                          <Button size="sm" variant="outline">{t("pay", "Pay")}</Button>
                          }
                        </div>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>

          {filteredObligations.length === 0 &&
          <div className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noObligationsFound", "No obligations found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};